import { Divider, Grid } from '@mui/material';
import { withStyles } from '@mui/styles';
import { escapeRegExp } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalLoader,
  RoutalPalette,
  RoutalSearchInput,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useState } from 'react';

import ExportIcon from '../../../../assets/img/export.svg';
import { RouteAvatarName } from '../../../../components/RouteAvatarName';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../context/shared/tableColumns';
import { Driver } from '../../../../domain/driver/Driver';
import { saveData } from '../../../../helpers/excel/Common';
import { exportDrivers as exportDriversToExcel } from '../../../../helpers/excel/Drivers';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../../react-components/Button';
import FormattedMessage from '../../../../react-components/FormattedCustomMessage';
import Text from '../../../../react-components/Text';
import VirtualizedDataTable from '../../shared/virtualized-datatable/VirtualizedDataTable';
import { DriversTableColumnKeys } from '../context/tableColumns/driversTableColumns';
import { DriverRightPanel } from '../driver-right-panel/DriverRightPanel';
import { useSetDriversColumns } from '../hooks/driver/useSetDriversColumns';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedDrivers } from '../hooks/plan-details/useSetActivePlanSelectedDrivers';
import { PlanDriversActionsMenu } from '../menus/DriversActionsMenu';
import DriversCreateMenu from '../menus/DriversCreateMenu';
import { getDriversColumns } from './PlanDriversListColumns';
import { PlanDriversListColumnsButton } from './PlanDriversListColumnsButton';

const styles = (_theme: any) => ({
  divInside: {
    height: `100%`,
    width: `100%`,
  },
  divContainer: {
    padding: `0`,
    backgroundColor: `white`,
    marginBottom: 0,
    height: `100%`,
    width: `100%`,
  },
  grid: {
    width: `100%`,
    display: `flex`,
  },
});

type IUnassignedStatus = `unassigned` | `assigned`;

const defaultUnassignedStatus: IUnassignedStatus[] = [`unassigned`, `assigned`];

type DriversListProps = {
  classes: {
    divInside: string;
    divContainer: string;
    grid: string;
  };
};

export const PlanDriversList = withStyles(styles)(({ classes }: DriversListProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: stops } = useRetrieveActivePlanStops();
  const { data: drivers, isLoading: isDriversLoading } = useRetrieveActivePlanDrivers();

  const { selectedDrivers, setSelectedDrivers } = useSetActivePlanSelectedDrivers();
  const { detailType, setRightPanelInfo } = useRetrieveRightPanelInfo();

  const [filterUnassigned, setFilterUnassigned] = useState<IUnassignedStatus[]>(defaultUnassignedStatus);
  const [filterText, setFilterText] = useState<string>(``);
  const [contextAnchorTableDrivers, setContextAnchorTableDrivers] = useState<
    | {
        screenPosition: {
          top: number;
          left: number;
        };
        drivers?: Driver[];
        driverClicked?: Driver;
      }
    | undefined
  >(undefined);
  const [sortRoutesInfo, setSortRoutesInfo] = useState<TableColumnsSortConfig<DriversTableColumnKeys>>({
    order: `desc`,
    orderBy: `created_at`,
  });

  const { tableColumns } = useSetDriversColumns();

  const filterDrivers = () => {
    let routesFiltered: Driver[] = drivers || [];

    if (routesFiltered.length > 0) {
      if (!filterUnassigned.includes(`assigned`)) {
        routesFiltered = [...routesFiltered.filter((r: Driver) => !r.stops || r.stops.length === 0)];
      }
      if (!filterUnassigned.includes(`unassigned`)) {
        routesFiltered = [...routesFiltered.filter((r: Driver) => !!r.stops && r.stops.length > 0)];
      }

      if (filterText !== ``) {
        const filterRegex = new RegExp(`${escapeRegExp(filterText).toLowerCase()}`, `ig`);
        routesFiltered = [
          ...routesFiltered.filter(
            (r: Driver) =>
              (r.label && r.label.match(filterRegex)) ||
              (r.start_location && r.start_location.label && r.start_location.label.match(filterRegex)) ||
              (r.end_location && r.end_location.label && r.end_location.label.match(filterRegex))
          ),
        ];
      }
    }
    return routesFiltered;
  };

  if (!activeProject) return null;

  const exportExcel = (all: boolean) => {
    const sortedDrivers = sortDataByTableColumns({
      data: all ? drivers ?? [] : filterDrivers(),
      tableColumns,
      sortInfo: sortRoutesInfo,
    });
    saveData(
      exportDriversToExcel({
        showRootParams: userCredentials?.test(`can_manage_backoffice`),
        drivers:
          selectedDrivers.length > 0
            ? sortedDrivers.filter((driver) => selectedDrivers.includes(driver.id))
            : sortedDrivers,
        project: activeProject!,
        tableColumns,
      }),
      `ExportDrivers_${activePlan?.label ?? ``}`
    );
  };

  if (isDriversLoading) {
    return (
      <Grid container alignItems="center" justifyContent="center" style={{ height: `100%`, width: `100%` }}>
        <RoutalLoader width={60} />
      </Grid>
    );
  }

  return (
    <div className={classes.divInside} style={{ display: `flex` }}>
      <Grid container direction="column" wrap="nowrap" className={classes.divContainer}>
        <Grid container style={{ padding: `8px 22px`, backgroundColor: RoutalPalette.oldWhite }}>
          <Grid item container alignItems="center" wrap="nowrap">
            <Grid container item>
              <RoutalSelect
                id="filter-routes-unassigned"
                containerStyle={{
                  maxWidth: `220px`,
                  margin: `6px 0`,
                }}
                style={{
                  justifyContent: `space-between`,
                }}
                popoverProps={{
                  options: defaultUnassignedStatus.map(
                    (status: IUnassignedStatus): RoutalSelectMultiSelectOption => ({
                      key: status,
                      checked: filterUnassigned.includes(status),
                      hideCheckIcon: false,
                      closeOnClick: true,
                      onClick: () => {
                        if (filterUnassigned?.includes(status)) {
                          const newFilterStatus = [
                            ...filterUnassigned.filter((st: IUnassignedStatus) => st !== status),
                          ];
                          setFilterUnassigned(newFilterStatus.length > 0 ? newFilterStatus : defaultUnassignedStatus);
                        } else {
                          setFilterUnassigned([...(filterUnassigned ?? []), status]);
                        }
                      },
                      content: (
                        <Text
                          style={{ cursor: `pointer` }}
                          intlId={`plans.plan.data.routes.header.filter.status.${status}`}
                        />
                      ),
                    })
                  ),
                }}
              >
                {filterUnassigned.length === 2 ? (
                  <Text intlId="all_plural" style={{ cursor: `pointer` }} />
                ) : (
                  <>
                    {filterUnassigned.map((status: IUnassignedStatus, idx: number, array: IUnassignedStatus[]) => (
                      <span key={idx}>
                        <FormattedMessage tagName="span" id={`plans.plan.data.routes.header.filter.status.${status}`} />
                        {idx !== array.length - 1 ? (
                          <>
                            <span style={{ marginRight: `4px` }}>,</span>
                            <HSpacer small />
                          </>
                        ) : null}
                      </span>
                    ))}
                  </>
                )}
              </RoutalSelect>
            </Grid>

            <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end" alignItems="center">
              {activePlan && (
                <>
                  <Grid item>
                    <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                      <RoutalSearchInput
                        inputContainerStyles={{
                          width: `260px`,
                        }}
                        onChange={_.debounce((text: any) => {
                          setFilterText(text);
                          // handleSearchRoutes();
                        }, 300)}
                        searchLive
                      />
                    </Grid>
                  </Grid>

                  <HSpacer medium />

                  <Grid item>
                    <DriversCreateMenu
                      id="list-routes-create-actions"
                      intlId="plans.plan.data.routes.add_new"
                      styleType="secondary"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
            style={{
              minHeight: `46px`,
            }}
          >
            <Grid item container alignItems="center">
              <Text
                variant="body"
                intlId={`${
                  selectedDrivers.length > 0
                    ? `plans.plan.data.routes.header.selected_info`
                    : `plans.plan.data.routes.header.num_rows`
                }`}
                intlValues={{
                  num: selectedDrivers.length,
                  total: (drivers ?? []).length ?? 0,
                }}
              />

              <HSpacer small />

              {stops && drivers && selectedDrivers.length > 0 ? (
                <PlanDriversActionsMenu
                  type="popover"
                  planStops={stops}
                  planDrivers={drivers}
                  activeProject={activeProject}
                  drivers={(drivers ?? []).filter((driver) => selectedDrivers.includes(driver.id))}
                  popoverProps={{
                    hideArrow: false,
                    style: {
                      justifyContent: `space-between`,
                    },
                    containerStyle: {
                      width: `240px`,
                    },
                    content: (
                      <Text
                        variant="body"
                        style={{ cursor: `pointer` }}
                        intlId="plans.plan.data.routes.header.actions"
                      />
                    ),
                  }}
                />
              ) : null}
            </Grid>

            <Grid item container alignItems="center" justifyContent="flex-end">
              <PlanDriversListColumnsButton id="plan-drivers-configurable-table-columns" />
              <HSpacer small />
              <RoutalTooltip title={intl.formatMessage({ id: `plans.plan.data.routes.header.export_excel` })}>
                <Button
                  styleType="secondary"
                  onClick={() => exportExcel(false)}
                  style={{
                    height: `35px`,
                    width: `35px`,
                    padding: 0,
                    margin: 0,
                    borderRadius: `5px`,
                  }}
                >
                  <img alt="" height="20px" width="20px" src={ExportIcon} draggable="false" />
                </Button>
              </RoutalTooltip>
            </Grid>
          </Grid>
        </Grid>

        <Divider />

        <Grid container direction="column" style={{ height: `100%`, position: `relative` }}>
          {activePlan && (
            <Grid
              item
              container
              className={classes.grid}
              style={{
                overflowX: `auto`,
                overflowY: `hidden`,
                height: `100%`,
              }}
            >
              {(drivers ?? []).length === 0 ? (
                <Grid container direction="column" alignItems="center" justifyContent="center" wrap="nowrap">
                  <Text intlId="plans.plan.live.content.driver.no_routes_placeholder.title" />
                </Grid>
              ) : (
                <Grid container style={{ width: `100%`, position: `relative` }}>
                  <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                    {filterDrivers().length > 0 ? (
                      <VirtualizedDataTable
                        docs={sortDataByTableColumns({
                          data: filterDrivers(),
                          tableColumns,
                          sortInfo: sortRoutesInfo,
                        }).map((planRoute: Driver) => {
                          const checkboxIcon = (
                            <RouteAvatarName
                              name={planRoute.getShortLabel()}
                              color={planRoute.color?.value}
                              additionalStyles={{ width: `20px`, height: `20px` }}
                            />
                          );
                          return {
                            doc: planRoute,
                            checkboxIcon,
                          };
                        })}
                        showCheckboxes
                        onRowDoubleClick={(driver: Driver) => {
                          setRightPanelInfo({
                            detailType: `driver`,
                            detailId: `${driver.id}`,
                          });
                        }}
                        handleOnContextMenu={(e: any, driver: Driver) => {
                          e.preventDefault();
                          const found = (drivers ?? []).find((driver) => selectedDrivers.includes(driver.id));
                          setContextAnchorTableDrivers({
                            screenPosition: {
                              top: e.clientY,
                              left: e.clientX,
                            },
                            drivers: found
                              ? (drivers ?? []).filter((driver) => selectedDrivers.includes(driver.id))
                              : [driver],
                            driverClicked: found ?? driver,
                          });
                        }}
                        onMultipleSelection={(docs: Array<Driver>) => setSelectedDrivers(docs.map((doc) => doc.id))}
                        selectedDocs={(drivers ?? []).filter((driver) => selectedDrivers.includes(driver.id))}
                        initialSortBy={sortRoutesInfo.orderBy}
                        initialSortOrder={sortRoutesInfo.order}
                        onColumnSort={(column: any, order: `asc` | `desc`) => {
                          setSortRoutesInfo({ order, orderBy: column });
                        }}
                        columns={[
                          ...getDriversColumns({
                            activeProject,
                            // plan: activePlan,
                            planStops: stops ?? [],
                            planDrivers: drivers ?? [],
                            showRootParams: userCredentials?.test(`can_manage_backoffice`),
                            tableColumns,
                            onOpenRightPanel: (e, doc) => {
                              e.stopPropagation();
                              setRightPanelInfo({
                                detailType: `driver`,
                                detailId: `${doc.id}`,
                              });
                            },
                          }),
                        ]}
                      />
                    ) : (
                      <Grid
                        container
                        direction="column"
                        alignItems="center"
                        style={{
                          height: `100%`,
                          padding: `32px`,
                        }}
                      >
                        <Text variant="body-large" intlId="no_results" />
                      </Grid>
                    )}
                  </div>
                </Grid>
              )}
            </Grid>
          )}

          {contextAnchorTableDrivers && contextAnchorTableDrivers.drivers && (
            <PlanDriversActionsMenu
              type="contextual"
              planStops={stops ?? []}
              planDrivers={drivers ?? []}
              activeProject={activeProject}
              drivers={
                selectedDrivers && selectedDrivers.length > 0
                  ? (drivers ?? []).filter((driver) => selectedDrivers.includes(driver.id))
                  : contextAnchorTableDrivers.drivers
              }
              contextAnchor={{ screenPosition: contextAnchorTableDrivers.screenPosition }}
              setContextAnchor={setContextAnchorTableDrivers}
            />
          )}
        </Grid>
      </Grid>
      {detailType === `driver` ? <DriverRightPanel /> : null}
    </div>
  );
});
